import { cls } from "@wss/cls";
import React, { ErrorInfo, HTMLAttributes, HtmlHTMLAttributes, KeyboardEvent, MouseEvent, SVGAttributes, SVGProps } from "react";
import { logException } from ".";
export * from "react-error-boundary";
export function FallbackContainer({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div {...rest} className={cls("max-w-md mx-auto mt-8", className)} data-sentry-component="FallbackContainer" data-sentry-source-file="error-boundary.tsx">
      {children}
    </div>;
}
export function SadComputerSVG({
  className,
  ...rest
}: SVGProps<SVGSVGElement>): JSX.Element {
  return <svg xmlns="http://www.w3.org/2000/svg" width="103" height="87" viewBox="0 0 103 87" className={cls("w-full", className)} {...rest} data-sentry-element="svg" data-sentry-component="SadComputerSVG" data-sentry-source-file="error-boundary.tsx">
      <g fill="#BDBFBC" fillRule="evenodd" data-sentry-element="g" data-sentry-source-file="error-boundary.tsx">
        <path d="M0 8c0-4.4 3.6-8 8-8h87c4.4 0 8 3.6 8 8v54c0 4.4-3.6 8-8 8H8c-4.4 0-8-3.6-8-8V8zm2 0v54c0 3.3 2.7 6 6 6h87c3.3 0 6-2.7 6-6V8c0-3.3-2.7-6-6-6H8C4.7 2 2 4.7 2 8z" data-sentry-element="path" data-sentry-source-file="error-boundary.tsx" />
        <path d="M6 11c0-2.8 2.2-5 5-5h81c2.8 0 5 2.2 5 5v39c0 2.8-2.2 5-5 5H11c-2.8 0-5-2.2-5-5V11zm2 0v39c0 1.7 1.3 3 3 3h81c1.7 0 3-1.3 3-3V11c0-1.7-1.3-3-3-3H11c-1.7 0-3 1.3-3 3z" data-sentry-element="path" data-sentry-source-file="error-boundary.tsx" />
        <path fillRule="nonzero" d="M46 38h-3c0-4.7 3.8-8 8.5-8s8.5 3.3 8.5 8h-3c0-3-2.3-5-5.5-5S46 35 46 38z" data-sentry-element="path" data-sentry-source-file="error-boundary.tsx" />
        <circle cx="57.5" cy="24.5" r="2.5" data-sentry-element="circle" data-sentry-source-file="error-boundary.tsx" />
        <circle cx="45.5" cy="24.5" r="2.5" data-sentry-element="circle" data-sentry-source-file="error-boundary.tsx" />
        <path d="M44 68h15c-.3 0 1.7 15 15 15-.4 0 1.6 4-3 4H32c-4.6 0-2.6-4-3-4 13.3 0 15.3-15 15-15z" data-sentry-element="path" data-sentry-source-file="error-boundary.tsx" />
      </g>
    </svg>;
}
export function FallbackHeading({
  className,
  ...rest
}: HTMLAttributes<HTMLHeadingElement>): JSX.Element {
  return <h1 {...rest} className={cls("mb-3 text-3xl text-center font-bold md:text-4xl", className)} data-sentry-component="FallbackHeading" data-sentry-source-file="error-boundary.tsx">
      Sorry about that.
    </h1>;
}
export function FallbackMessage({
  className,
  ...rest
}: HTMLAttributes<HTMLParagraphElement>): JSX.Element {
  return <p {...rest} className={cls("text-lg mb-6 text-center", className)} data-sentry-component="FallbackMessage" data-sentry-source-file="error-boundary.tsx">
      You have encountered a technical error on our site. Our engineers have
      been notified and will be looking into it shortly. We apologize for the
      inconvenience.
    </p>;
}
export function FallbackButtonContainer({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cls("w-full flex justify-center gap-x-4", className)} {...rest} data-sentry-component="FallbackButtonContainer" data-sentry-source-file="error-boundary.tsx">
      {children}
    </div>;
}
export function FallbackGoBackButton({
  className,
  ...rest
}: HTMLAttributes<HTMLButtonElement>): JSX.Element {
  function handleGoBack(event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    window.history.back();
  }
  function handleDisableSpace(e: KeyboardEvent<HTMLButtonElement>): void {
    if (e.code === "Space") e.preventDefault();
  }
  return <button type="button" role="link" onKeyDown={handleDisableSpace} className={cls("text-neutral-700 text-sm font-semibold bg-neutral-100 border-stone-300 py-1.5 px-4 mt-1.5 mx-1 rounded border-solid border hover:border-neutral-400", className)} onClick={handleGoBack} {...rest} data-sentry-component="FallbackGoBackButton" data-sentry-source-file="error-boundary.tsx">
      Go Back
    </button>;
}
export function FallbackChatNowButton({
  className,
  onClick,
  ...rest
}: HTMLAttributes<HTMLAnchorElement>): JSX.Element {
  function handleOpenChat(e: MouseEvent<HTMLAnchorElement>): boolean {
    if (onClick) {
      onClick(e);
    } else {
      window.open("/chat/", "", "width=650,height=700,resizable=1,scrollbars=1");
    }
    return false;
  }
  return <a href="/chat/" target="_blank" color="blue" title="Contact WebstaurantStore" className={cls("bg-blue-700 border-blue-700 border text-white text-sm py-1.5 px-4 mt-1.5 mx-1 rounded border-solid font-semibold hover:border-[#1C5165]", className)} {...rest} onClick={handleOpenChat} data-sentry-component="FallbackChatNowButton" data-sentry-source-file="error-boundary.tsx">
      Chat Now
    </a>;
}
export type PageFallbackProps = {
  containerProps?: HTMLAttributes<HTMLDivElement>;
  svgProps?: SVGAttributes<SVGSVGElement>;
  headingProps?: HTMLAttributes<HTMLHeadingElement>;
  messageProps?: HTMLAttributes<HTMLParagraphElement>;
  buttonContainerProps?: HtmlHTMLAttributes<HTMLDivElement>;
  goBackProps?: HTMLAttributes<HTMLButtonElement>;
  chatNowProps?: HTMLAttributes<HTMLAnchorElement>;
};
export function PageFallback({
  containerProps,
  svgProps,
  headingProps,
  messageProps,
  buttonContainerProps,
  goBackProps,
  chatNowProps
}: PageFallbackProps): JSX.Element {
  return <FallbackContainer {...containerProps} data-sentry-element="FallbackContainer" data-sentry-component="PageFallback" data-sentry-source-file="error-boundary.tsx">
      <SadComputerSVG {...svgProps} data-sentry-element="SadComputerSVG" data-sentry-source-file="error-boundary.tsx" />
      <FallbackHeading {...headingProps} data-sentry-element="FallbackHeading" data-sentry-source-file="error-boundary.tsx" />
      <FallbackMessage {...messageProps} data-sentry-element="FallbackMessage" data-sentry-source-file="error-boundary.tsx" />
      <FallbackButtonContainer {...buttonContainerProps} data-sentry-element="FallbackButtonContainer" data-sentry-source-file="error-boundary.tsx">
        <FallbackGoBackButton {...goBackProps} data-sentry-element="FallbackGoBackButton" data-sentry-source-file="error-boundary.tsx" />
        <FallbackChatNowButton {...chatNowProps} data-sentry-element="FallbackChatNowButton" data-sentry-source-file="error-boundary.tsx" />
      </FallbackButtonContainer>
    </FallbackContainer>;
}
export function logErrorBoundaryToSentry(error: Error, info: ErrorInfo): void {
  let message = "There was an error rendering a component.";
  if (info.componentStack) {
    message += `\nComponent Stack:\n${info.componentStack}`;
  } else {
    message += `\nMissing Component Stack`;
  }
  logException(new Error(message, {
    cause: error
  }));
}